import { ReactNode, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Skeleton, Space, Tabs } from 'antd';

import { useRouteMatchParam, useWindowSize } from '@/hooks';

import { RouteType } from '@/types/RouteType';
import PageTabs from '@/components/_common/PageTabs';
import Authorities, { AuthoritiesType } from '@/types/Authority';
import AccessDenied from '@/components/_common/AccessDenied';
import { getUserFromLocalStorage } from '@/utils/user';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import SkeletonCustom from '@/components/_common/SkeletonCustom';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import UserGuiding from '@/core/UserGuinding';

interface WorkOrderComplementTabsProps {
  children: ReactNode;
  route: RouteType;
  isLoading: boolean;
  extra?: ReactNode;
  permissions?: AuthoritiesType[];
}

function WorkOrderComplementTabs({
  children,
  route,
  isLoading,
  extra,
  permissions,
}: WorkOrderComplementTabsProps) {
  const intl = useIntl();
  const history = useHistory();
  const { isMobile } = useWindowSize();

  const workOrderId = useRouteMatchParam('workOrderId');
  const complementId = useRouteMatchParam('complementId');

  const { employeeId } = getUserFromLocalStorage();

  const handleTabChange = (key: string) => {
    history.push(
      `/app/movements/workOrders/${workOrderId}/${key.replace(
        ':complementId',
        complementId,
      )}`,
    );
  };

  const historyKeys = history?.location.pathname.split('/');
  const startIndex = historyKeys.indexOf(`complements`);
  const selectedTabKey = `complements/:complementId/${
    historyKeys[startIndex + 2]
  }`;

  const {
    complement: { data: complement, isLoading: isLoadingComplement },
  } = useWorkorderDetailContext();

  const isComplementOpen =
    complement?.status === WorkOrderComplementStatus.Open;

  const isComplementAuthorized =
    complement?.status === WorkOrderComplementStatus.Authorized;

  useEffect(() => {
    if (isComplementOpen) {
      UserGuiding.showWorkOrderAuthorizeGuide();
    } else if (isComplementAuthorized) {
      UserGuiding.showWorkOrderCloseGuide();
    }
  }, [isComplementOpen, isComplementAuthorized]);

  if (
    !isLoadingComplement &&
    complement?.estimator.id !== Number(employeeId) &&
    !hasPermissions([
      Authorities.MOVIMENTATION_WORKORDER_EDIT_OF_OTHER_BUDGETIST,
    ])
  ) {
    return <AccessDenied permission={route.permissions} />;
  }

  return (
    <SkeletonCustom loading={isLoadingComplement} skeletonCount={4}>
      <PageTabs
        onChange={handleTabChange}
        defaultActiveKey={selectedTabKey}
        tabBarExtraContent={extra || null}
      >
        {route.routes.map((item) => (
          <Tabs.TabPane
            key={item.key}
            tab={
              permissions && hasPermissions(permissions) ? (
                intl.formatMessage({ id: item.name })
              ) : (
                <Space>{intl.formatMessage({ id: item.name })}</Space>
              )
            }
          />
        ))}
      </PageTabs>

      <div className="tab-wrapper">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Skeleton active loading={isLoading}>
            {permissions && !hasPermissions(permissions) ? (
              <AccessDenied permission={permissions} />
            ) : (
              <>
                {extra && isMobile && (
                  <div style={{ marginBottom: 24 }}>{extra}</div>
                )}

                {children}
              </>
            )}
          </Skeleton>
        </motion.div>
      </div>
    </SkeletonCustom>
  );
}

export default WorkOrderComplementTabs;
